/**
 * New Candidate Analysis Service
 * JD-based dynamic analysis - no static classification
 * JD is mandatory and drives all competency mapping
 */
import JDBasedAnalysisService from './JDBasedAnalysisService';
import { getApiBaseUrl } from './ApiUtils';

/**
 * Analyze candidate with JD-based dynamic competencies
 * JD is mandatory - all analysis is based on JD requirements
 * @param {string} email - Candidate email
 * @param {string} role - Role (extracted from JD or manual)
 * @param {Array<string>} examCodes - Exam codes (2-5)
 * @param {string} jdText - Job description text extracted from PDF (MANDATORY)
 * @param {string} evaluationParams - Optional evaluation parameters
 * @returns {Promise<Object>} Analysis result
 */
const analyzeCandidateWithJD = async (email, role, examCodes, jdText, evaluationParams = '') => {
  try {
    // Validate inputs
    if (!email || !examCodes || examCodes.length === 0) {
      throw new Error('Email and exam codes are required');
    }

    if (!jdText || !jdText.trim()) {
      throw new Error('Job Description is mandatory for analysis');
    }

    if (examCodes.length < 2) {
      throw new Error('Minimum 2 exam codes are required');
    }

    if (examCodes.length > 5) {
      throw new Error('Maximum 5 exam codes are allowed per analysis');
    }

    // Process email: replace dots with commas for Firebase search
    const processedEmail = email.trim().replace(/\./g, ',');

    // Step 1: Fetch and map assessment data to JD competencies
    const assessmentData = await JDBasedAnalysisService.fetchAndMapAssessmentData(
      processedEmail,
      examCodes,
      jdText
    );

    // Step 2: Process assessment data
    const processedData = JDBasedAnalysisService.processJDBasedAssessment(
      assessmentData,
      email,
      role,
      examCodes
    );

    // Step 3: Generate JD-based AI analysis
    const aiContent = await generateJDBasedAIContent(
      processedData,
      jdText,
      evaluationParams
    );

    // Combine processed data with AI content
    return {
      ...processedData,
      ...aiContent
    };

  } catch (error) {
    console.error('JD-based candidate analysis error:', error);
    throw error;
  }
};

/**
 * Generate JD-based AI analysis
 * Focuses on suitability, gaps, and improvement areas based on JD
 */
const generateJDBasedAIContent = async (processedData, jdText, evaluationParams = '') => {
  try {
    const apiUrl = getApiBaseUrl();
    
    const response = await fetch(`${apiUrl}/api/jd-based-analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        candidateData: {
          candidateName: processedData.candidateName,
          candidateEmail: processedData.candidateEmail,
          role: processedData.role
        },
        competencyScores: processedData.competencyScores,
        jdCompetencies: processedData.jdCompetencies,
        jdText: jdText.substring(0, 5000), // Limit JD text
        evaluationParams
      })
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success) {
      return result.data;
    } else {
      throw new Error(result.error || 'Unknown API error');
    }

  } catch (error) {
    console.error('JD-based AI content generation error:', error);
    return null;
  }
};

const NewCandidateAnalysisService = {
  analyzeCandidateWithJD,
  generateJDBasedAIContent
};

export default NewCandidateAnalysisService;

